import React, { useState, useRef, useLayoutEffect } from 'react'
import { Box } from '@mui/material'
import { ReactComponent as ClockIcon } from '../../assets/appointment-icons/clock.svg'
import { ReactComponent as ChevronIcon } from '../../assets/appointment-icons/chevron.svg'
import { ReactComponent as TickIcon } from '../../assets/appointment-icons/tick.svg'
import { useLavMobile } from './LavDropdown'

/**
 * TimeListPicker — the "Time · B · single list (default)" component recreated
 * from the Claude Design "Add Appointment Flow" (ApTimePicker, variant="list").
 * A trigger button reveals a single scrolling column of slots grouped
 * under sticky period headers (Morning / Afternoon / Evening); the selected slot
 * is highlighted with a check and scrolled into view on open.
 *
 * @param {Object} props
 * @param {string} props.value - Selected time as "HH:MM" (24h) or '' / null
 * @param {Function} props.onChange - Called with the new "HH:MM" string
 * @param {number} [props.step=30] - Minutes between offered slots
 * @param {boolean} [props.small] - Compact trigger height
 * @param {boolean} [props.up] - Open the popover above the trigger
 * @param {'left'|'right'} [props.align='left'] - Horizontal alignment of popover
 * @param {boolean} [props.defaultOpen] - Start open
 * @param {boolean} [props.pinned] - Always open, rendered inline (no backdrop)
 * @param {boolean} [props.disabled] - Disable the trigger
 * @param {Object} [props.sx] - Extra sx applied to the root container
 */

// Lavender design tokens (shared vocabulary with the checkout drawer)
const L = {
	surface: '#F5F3FB',
	border: '#E7E2F3',
	text: '#2E2A40',
	muted: '#8B85A4',
	primary: '#7C6BD9',
	primaryDeep: '#5F4EC2',
	primarySoft: '#EDE9FB',
	field: '#FFFFFF',
	font: "'Outfit', sans-serif",
}

// Full 24-hour coverage — merchants can book at any time of day.
const TIME_SEGS = [
	{ id: 'early', label: 'Early morning', from: 0, to: 6 * 60 },
	{ id: 'am', label: 'Morning', from: 6 * 60, to: 12 * 60 },
	{ id: 'pm', label: 'Afternoon', from: 12 * 60, to: 18 * 60 },
	{ id: 'eve', label: 'Evening', from: 18 * 60, to: 24 * 60 },
]

function timeToMins(t) {
	const [h, m] = (t || '').split(':').map(Number)
	if (Number.isNaN(h)) return null
	return (h || 0) * 60 + (m || 0)
}
function minsToVal(m) {
	return String(Math.floor(m / 60)).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0')
}
function fmtClock(mins) {
	const h = Math.floor(mins / 60) % 24, m = mins % 60
	return (((h + 11) % 12) + 1) + ':' + String(m).padStart(2, '0') + ' ' + (h >= 12 ? 'PM' : 'AM')
}

// Icon set sourced from src/assets/appointment-icons (exact design glyphs)
const ICONS = { clock: ClockIcon, chevron: ChevronIcon, tick: TickIcon }
function Icon({ name, size = 15, sw }) {
	const Cmp = ICONS[name]
	if (!Cmp) return null
	return (
		<Cmp
			width={size}
			height={size}
			{...(sw != null ? { strokeWidth: sw } : {})}
			style={{ flex: 'none', display: 'block' }}
			aria-hidden="true"
		/>
	)
}

export default function TimeListPicker({
	value,
	onChange,
	step = 30,
	small = false,
	up = false,
	align = 'left',
	defaultOpen = false,
	pinned = false,
	disabled = false,
	sx = {},
}) {
	// Touch sizing (design `.mbap` overrides) when rendered inside a mobile shell.
	const mobile = useLavMobile()
	const [open, setOpen] = useState(Boolean(defaultOpen) || Boolean(pinned))
	const listRef = useRef(null)
	const valMins = value ? timeToMins(value) : null

	// Scroll the selected slot into view whenever the list opens
	useLayoutEffect(() => {
		if (!open) return
		const c = listRef.current
		const el = c ? c.querySelector('[data-sel="true"]') : null
		if (c && el) c.scrollTop = Math.max(0, el.offsetTop - 104)
	}, [open])

	const pick = (m) => {
		onChange?.(minsToVal(m))
		if (!pinned) setOpen(false)
	}

	// Mobile touch sizes: regular triggers 50px, `small` (inline visit editor) 44px.
	const triggerSx = {
		display: 'flex', alignItems: 'center', gap: small ? '7px' : '9px', width: '100%',
		height: small ? (mobile ? '44px' : '36px') : (mobile ? '50px' : '42px'),
		px: small ? (mobile ? '11px' : '10px') : (mobile ? '13px' : '12px'),
		border: `1.5px solid ${open ? L.primary : L.border}`,
		borderRadius: small ? (mobile ? '11px' : '10px') : (mobile ? '12px' : '10px'),
		bgcolor: L.field, fontFamily: L.font,
		fontSize: small ? (mobile ? '13px' : '12.5px') : (mobile ? '15px' : '14px'),
		fontWeight: 600, color: L.text, cursor: disabled ? 'default' : 'pointer',
		boxShadow: open ? `0 0 0 3px ${L.primarySoft}` : 'none',
		opacity: disabled ? 0.55 : 1,
		transition: 'border-color .15s, box-shadow .15s',
		'&:hover': { borderColor: disabled ? L.border : L.primary },
		'& > svg': { color: L.muted },
	}

	// .ap-pop + .ap-listpop: full-width popover, 6px padding
	const popSx = {
		position: 'absolute', zIndex: 30, bgcolor: '#FFFFFF',
		border: `1px solid ${L.border}`, borderRadius: mobile ? '16px' : '14px', p: '6px',
		left: 0, right: 0,
		boxShadow: '0 14px 36px rgba(95, 78, 194, 0.16), 0 3px 10px rgba(95, 78, 194, 0.08)',
		...(up ? { bottom: 'calc(100% + 6px)' } : { top: 'calc(100% + 6px)' }),
	}

	return (
		<Box sx={{ position: 'relative', fontFamily: L.font, ...sx }}>
			<Box
				component="button"
				type="button"
				aria-expanded={open}
				disabled={disabled}
				onClick={() => { if (!disabled) setOpen(pinned ? true : !open) }}
				sx={triggerSx}
			>
				<Icon name="clock" size={small ? 14 : 15} />
				<Box component="span" sx={{
					flex: 1, textAlign: 'left', minWidth: 0, overflow: 'hidden',
					textOverflow: 'ellipsis', whiteSpace: 'nowrap',
					color: valMins != null ? L.text : L.muted, fontWeight: valMins != null ? 600 : 500,
				}}>
					{valMins != null ? fmtClock(valMins) : 'Pick a time'}
				</Box>
				<Box component="span" sx={{
					display: 'flex', color: L.muted, transition: 'transform .15s',
					transform: open ? 'rotate(180deg)' : 'none',
				}}>
					<Icon name="chevron" size={14} />
				</Box>
			</Box>

			{open && (
				<>
					{!pinned && (
						<Box
							onClick={() => setOpen(false)}
							sx={{ position: 'fixed', inset: 0, zIndex: 25 }}
						/>
					)}
					<Box sx={popSx}>
						<Box
							ref={listRef}
							sx={{
								position: 'relative', maxHeight: mobile ? '300px' : '276px', overflowY: 'auto',
								display: 'flex', flexDirection: 'column', gap: '1px',
								scrollbarWidth: 'thin', scrollbarColor: `${L.border} transparent`,
							}}
						>
							{TIME_SEGS.map((g) => {
								const slots = []
								for (let m = g.from; m < g.to; m += step) slots.push(m)
								return (
									<React.Fragment key={g.id}>
										<Box sx={{
											position: 'sticky', top: 0, zIndex: 1, bgcolor: '#FFFFFF',
											px: mobile ? '12px' : '10px', pt: mobile ? '8px' : '6px', pb: mobile ? '5px' : '4px',
											fontSize: mobile ? '10.5px' : '10px', fontWeight: 750,
											letterSpacing: '0.07em', textTransform: 'uppercase', color: L.muted,
											fontFamily: L.font,
										}}>
											{g.label}
										</Box>
										{slots.map((m) => {
											const isSel = m === valMins
											return (
												<Box
													component="button"
													type="button"
													key={m}
													data-sel={isSel ? 'true' : 'false'}
													onClick={() => pick(m)}
													sx={{
														display: 'flex', alignItems: 'center', justifyContent: 'space-between',
														flex: 'none', px: mobile ? '12px' : '10px', py: mobile ? '12px' : '8px',
														border: 'none', borderRadius: mobile ? '10px' : '8px',
														bgcolor: isSel ? L.primarySoft : 'transparent',
														color: isSel ? L.primaryDeep : L.text,
														fontFamily: L.font, fontSize: mobile ? '14.5px' : '12.5px', fontWeight: isSel ? 750 : 600,
														textAlign: 'left', fontVariantNumeric: 'tabular-nums', cursor: 'pointer',
														'&:hover': { bgcolor: isSel ? L.primarySoft : L.surface },
													}}
												>
													<span>{fmtClock(m)}</span>
													{isSel && (
														<Box component="span" sx={{ display: 'flex', ml: '8px', color: L.primaryDeep }}>
															<Icon name="tick" size={13} sw={2.2} />
														</Box>
													)}
												</Box>
											)
										})}
									</React.Fragment>
								)
							})}
						</Box>
					</Box>
				</>
			)}
		</Box>
	)
}
